<template>
  <div>
    <VmsPageHeader
      kicker="Reports"
      title="Visitors Report"
      subtitle="Review visit history, export records, and spot check-in trends."
      icon="mdi-chart-box-plus-outline"
    >
      <template #actions>
        <v-text-field
          v-if="showSearch"
          v-model="filterReportBySearch"
          prepend-icon="mdi-magnify"
          append-outer-icon="mdi-close"
          @click:append-outer="showSearch = false"
          style="max-width: 260px"
          hide-details
          dense
          outlined
        ></v-text-field>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="showSearch = !showSearch">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span class="text-caption">Search</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" :loading="downloading" @click="downloadCSV">
              <v-icon>mdi-cloud-download</v-icon>
            </v-btn>
          </template>
          <span class="text-caption">Download CSV</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" onclick="window.print()">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </template>
          <span class="text-caption">Print</span>
        </v-tooltip>

        <v-menu :close-on-content-click="closeOnContentClick">
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn icon v-bind="attrs" v-on="{ ...tooltip, ...menu }" @click="closeOnContentClick = false">
                  <v-icon>mdi-view-column</v-icon>
                </v-btn>
              </template>
              <span class="text-caption">View Columns</span>
            </v-tooltip>
          </template>

          <v-card>
            <v-card-title class="pl-5">
              <span class="grey--text body-1">Show Columns</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="closeOnContentClick = true">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-checkbox
                v-for="(header, index) in headers"
                :key="`report-header-${header.value || header.text}-${index}`"
                :label="`${header.text.toString()}`"
                class="my-n2 pa-n5"
              ></v-checkbox>
            </v-card-text>
          </v-card>
        </v-menu>

        <v-menu :close-on-content-click="closeOnContentClick">
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn icon v-bind="attrs" v-on="{ ...tooltip, ...menu }" @click="closeOnContentClick = false">
                  <v-icon>mdi-filter-variant</v-icon>
                </v-btn>
              </template>
              <span class="text-caption">Filter Table</span>
            </v-tooltip>
          </template>

          <v-card>
            <v-card-title class="pl-5">
              <span class="grey--text body-1">Filters</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="closeOnContentClick = true">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-form class="pa-5">
              <v-row>
                <v-col>
                  <v-autocomplete 
                    label="Host" 
                    :items="hosts" 
                    @change="onFilterSelect" 
                    v-model="filters.host_id"
                    item-text="name"
                    item-value="member_code"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col>
                  <v-autocomplete 
                    label="Host Organization" 
                    :items="organizations" 
                    @change="onFilterSelect" 
                    v-model="filters.organization_id"
                    item-text="legal_name"
                    item-value="id"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-dialog ref="datePickerDialog" v-model="datePickerModal" :return-value.sync="filters.filter_date" persistent width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field 
                        v-model="dateRangeText" 
                        label="Date Range" 
                        prepend-icon="mdi-calendar" 
                        readonly
                        v-bind="attrs" 
                        v-on="on"
                        clearable
                        @click:clear="
                          filters.filter_date = []; 
                          activeDatePreset = null;
                          onFilterSelect();
                        "
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="filters.filter_date" range scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="datePickerModal = false">
                        Cancel
                      </v-btn>
                      <v-btn text color="primary" @click="onDatePickerSelect">
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <v-col>
                  <v-dialog ref="dialog" v-model="timePickerModal" :return-value.sync="filterTime" persistent
                    width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field 
                        v-model="filters.filter_time" 
                        label="Time" 
                        prepend-icon="mdi-clock-time-four-outline"
                        readonly 
                        v-bind="attrs" 
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker v-if="timePickerModal" v-model="filters.filter_time" full-width scrollable>
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="timePickerModal = false">
                        Cancel
                      </v-btn>
                      <v-btn text color="primary" @click="onTimePickerSelect">
                        OK
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-autocomplete 
                    label="Facility" 
                    :items="facilities" 
                    @change="onFilterSelect" 
                    v-model="filters.facility_id"
                    item-text="attributes.facility"
                    item-value="id"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col>
                  <v-autocomplete 
                    label="Guest" 
                    :items="guests" 
                    @change="onFilterSelect" 
                    v-model="filters.guest_id"
                    :loading="isLoading"
                    :search-input.sync="search"
                    hide-no-data
                    hide-selected
                    item-text="name"
                    item-value="id"
                    placeholder="Start typing to Search"
                    prepend-icon="mdi-database-search"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-autocomplete 
                    label="Security" 
                    :items="watchmenList" 
                    item-text="attributes.name" 
                    item-value="id"
                    @change="onFilterSelect" 
                    v-model="filters.watchman_id"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col>
                </v-col>
              </v-row>
            </v-form>
          </v-card>
        </v-menu>
      </template>
    </VmsPageHeader>

    <div class="vms-date-presets">
      <v-chip
        small
        :outlined="activeDatePreset !== 'today'"
        :color="activeDatePreset === 'today' ? 'info' : undefined"
        :dark="activeDatePreset === 'today'"
        @click="applyDatePreset('today')"
      >Today</v-chip>
      <v-chip
        small
        :outlined="activeDatePreset !== 'week'"
        :color="activeDatePreset === 'week' ? 'info' : undefined"
        :dark="activeDatePreset === 'week'"
        @click="applyDatePreset('week')"
      >This week</v-chip>
      <v-chip
        small
        :outlined="activeDatePreset !== 'month'"
        :color="activeDatePreset === 'month' ? 'info' : undefined"
        :dark="activeDatePreset === 'month'"
        @click="applyDatePreset('month')"
      >This month</v-chip>
      <v-chip
        small
        outlined
        v-if="filters.filter_date && filters.filter_date.length"
        @click="clearDatePreset"
      >
        <v-icon left x-small>mdi-close</v-icon>
        Clear dates
      </v-chip>
    </div>

    <div class="vms-insight-strip">
      <div class="vms-insight-card">
        <div class="vms-insight-card__label">Total Visits</div>
        <div class="vms-insight-card__value">{{ totalReportsCount }}</div>
        <div class="vms-insight-card__hint">{{ listVisitors.length }} shown on this page</div>
      </div>
      <div class="vms-insight-card">
        <div class="vms-insight-card__label">Checked In</div>
        <div class="vms-insight-card__value">{{ checkedInCount }}</div>
        <div class="vms-insight-card__hint">On premises, not checked out</div>
      </div>
      <div class="vms-insight-card">
        <div class="vms-insight-card__label">Checked Out</div>
        <div class="vms-insight-card__value">{{ checkedOutCount }}</div>
        <div class="vms-insight-card__hint">Completed visits on this page</div>
      </div>
      <div class="vms-insight-card" v-if="avgDurationLabel">
        <div class="vms-insight-card__label">Avg. Duration</div>
        <div class="vms-insight-card__value">{{ avgDurationLabel }}</div>
        <div class="vms-insight-card__hint">Across completed visits shown</div>
      </div>
    </div>

    <div class="vms-chart-grid mb-4" v-if="statusBreakdownChart || facilityBreakdownChart">
      <div class="vms-chart-span-6" v-if="statusBreakdownChart">
        <v-card class="vms-surface" elevation="0">
          <v-card-title class="vms-page-title" style="font-size: 1.05rem">
            Status Breakdown
          </v-card-title>
          <v-card-text>
            <GChart
              type="PieChart"
              loading="true"
              :data="statusBreakdownChart"
              :options="statusChartOptions"
            />
          </v-card-text>
        </v-card>
      </div>

      <div class="vms-chart-span-6" v-if="facilityBreakdownChart">
        <v-card class="vms-surface" elevation="0">
          <v-card-title class="vms-page-title" style="font-size: 1.05rem">
            Facility Breakdown
          </v-card-title>
          <v-card-text>
            <GChart
              type="PieChart"
              loading="true"
              :data="facilityBreakdownChart"
              :options="facilityChartOptions"
            />
          </v-card-text>
        </v-card>
      </div>
    </div>

    <v-card class="vms-surface" elevation="0">
      <VmsResponsiveList
        :items="listVisitors"
        :loading="setIsLoading"
        loading-text="Loading reports…"
        item-key="id"
      >
        <template #table>
          <v-data-table 
            :headers="headersMap" 
            :items="listVisitors" 
            class="elevation-0"
            :loading="setIsLoading"
            loading-text="Loading... Please wait" 
            :search="filterReportBySearch"
            :custom-filter="tableSearchFilter"
            show-select 
            :single-select="singleSelect"
            v-model="selected"
            :server-items-length="reportsPagination?.total"
            :options.sync="options"
          >
            <template v-slot:[`item.organization`]="{ item }">
              <span @click="selectOrganization(item)">{{ item.legal_name }}</span>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <div>
                <v-chip v-if="item.checkin_time && item.checkout_time" pill color="orange" text-color="white">
                  {{ item.visitor.status }}
                </v-chip>
                <v-chip v-if="item.checkin_time && !item.checkout_time" pill color="green" text-color="white">
                  {{ item.visitor.status }}
                </v-chip>
                <v-chip v-if="!item.checkin_time && !item.checkout_time" pill color="info" text-color="white">
                  {{ item.visitor.status }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.view_more`]="{ item }">
              <VmsIconAction
                icon="mdi-dots-horizontal"
                label="View"
                @click="viewVisit(item.id)"
              />
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <div class="my-1 ml-auto">
                <VmsIconAction
                  icon="mdi-pencil-outline"
                  label="Edit"
                  @click="editItem(item)"
                />
                <VmsIconAction
                  icon="mdi-delete-outline"
                  label="Delete"
                  color="error"
                  @click="deleteItem(item)"
                />
              </div>
            </template>

            <template v-slot:no-data>
              <a class="info--text text--darken-1">
                No Reports
              </a>
            </template>
          </v-data-table>
        </template>

        <template #card="{ item }">
          <div class="vms-list-card__title">{{ item.visitor?.name }}</div>
          <div class="vms-list-card__meta">
            <span><v-icon x-small class="mr-1">mdi-account-tie-outline</v-icon>{{ item.host?.name }}</span>
            <span><v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ item.host_facility }}</span>
          </div>

          <div class="vms-list-card__row">
            <span class="vms-list-card__label">Status</span>
            <span>
              <v-chip v-if="item.checkin_time && item.checkout_time" x-small pill color="orange" text-color="white">
                {{ item.visitor?.status }}
              </v-chip>
              <v-chip v-else-if="item.checkin_time && !item.checkout_time" x-small pill color="green" text-color="white">
                {{ item.visitor?.status }}
              </v-chip>
              <v-chip v-else x-small pill color="info" text-color="white">
                {{ item.visitor?.status }}
              </v-chip>
            </span>
          </div>

          <div class="vms-list-card__row">
            <span class="vms-list-card__label">Check-in</span>
            <span>{{ item.checkin_time || '—' }}</span>
          </div>

          <div class="vms-list-card__row">
            <span class="vms-list-card__label">Check-out</span>
            <span>{{ item.checkout_time || '—' }}</span>
          </div>

          <div class="vms-list-card__actions">
            <VmsIconAction
              icon="mdi-dots-horizontal"
              label="View"
              @click="viewVisit(item.id)"
            />
          </div>
        </template>

        <template #empty>
          <a class="info--text text--darken-1">No Reports</a>
        </template>
      </VmsResponsiveList>
    </v-card>
  </div>
</template>
<script>
import WatchmenService from '../../services/watchmen.service';
import OrganizationService from '../../services/organization.service';
import FacilitiesService from '../../services/facilities.service';
import UserService from '../../services/user.service';
import ReportsService from '../../services/reports.service';
import VmsPageHeader from '../../components/VmsPageHeader.vue';
import VmsResponsiveList from '../../components/VmsResponsiveList.vue';
import VmsIconAction from '../../components/VmsIconAction.vue';
import moment from 'moment';

export default {
  name: "Reports",

  components: {
    VmsPageHeader,
    VmsResponsiveList,
    VmsIconAction,
  },

  data: () => ({
    name: "Reports",
    watchmenList: [],
    loading: false,
    downloading: false,
    timePickerModal: false,
    isLoading: false,
    search: null,
    activeDatePreset: null,
    filters: {
      host_id: null,
      organization_id: null,
      filter_date: [],
      filter_time: null,
      facility_id: null,
      guest_id: null,
      watchman_id: null,
    },
    defaultFilters: {
      host_id: null,
      organization_id: null,
      filter_date: [],
      filter_time: null,
      facility_id: null,
      guest_id: null,
      watchman_id: null,
    },
    filterTime: null,
    showSearch: false,
    filterDate: null,
    datePickerModal: false,
    watchmanId: null,
    filterReportBySearch: null,
    search_term: '',
    searchDebounce: null,
    closeOnContentClick: false,
    checkbox: true,
    items: [],
    headers: [],
    selectedHeaders: [],
    options: {},
    headersMap: [
      {
        text: "Visitor",
        align: "start",
        filterable: true,
        value: "visitor.name",
      },
      {
        text: "Host",
        value: "host.name",
      },
      {
        text: "CheckIn",
        value: "checkin_time",
      },
      {
        text: "CheckOut",
        value: "checkout_time",
      },
      {
        text: "Time",
        value: "duration",
      },
      {
        text: "Facility",
        value: "host_facility",
      },
      {
        text: "Reason",
        value: "reason",
      },
      {
        text: "Status",
        value: "status",
      },
      {
        text: "View",
        value: "view_more"
      }
    ],
    singleSelect: false,
    selected: [],
    csvData: [[4, 5, 6], [7, 8, 9]],
    statusChartOptions: {
      title: "Status Breakdown",
      subtitle: "Visits shown on this page, by status",
      is3D: false,
      pieHole: 0.4,
      legend: {
        position: "bottom",
      },
    },
    facilityChartOptions: {
      title: "Facility Breakdown",
      subtitle: "Visits shown on this page, by facility",
      is3D: false,
      pieHole: 0.4,
      legend: {
        position: "bottom",
      },
    },
    // Filters
    hosts: [],
    organizations: [],
    date: [],
    time: [],
    facilities: [],
    guests: []
  }),

  computed: {
    dateRangeText() {
      if (!this.filters.filter_date || this.filters.filter_date.length === 0) return '';

      const sorted = [...this.filters.filter_date].sort();
      return sorted.join(' ~ ');
    },
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    visitorsList() {
      return this.$store.state.reports.reports;
    },
    showHeaders() {
      return (this.headers).filter(header =>
        this.selectedHeaders.includes(header)
      );
    },
    listVisitors() {
      return this.$store.getters["reports/getAllReports"]
    },
    setIsLoading() {
      return this.$store.getters["reports/setIsLoading"];
    },
    reportsPagination() {
      return this.$store.getters["reports/getReportsPagination"];
    },
    org() {
      return JSON.parse(localStorage.getItem("org"));
    },

    totalReportsCount() {
      return this.reportsPagination?.total ?? (this.listVisitors || []).length;
    },

    checkedInCount() {
      return (this.listVisitors || []).filter(
        (item) => item.checkin_time && !item.checkout_time
      ).length;
    },

    checkedOutCount() {
      return (this.listVisitors || []).filter(
        (item) => item.checkin_time && item.checkout_time
      ).length;
    },

    avgDurationLabel() {
      const list = this.listVisitors || [];
      const minutesList = list
        .map((item) => this.parseDurationMinutes(item.duration))
        .filter((value) => value !== null && !Number.isNaN(value));

      if (!minutesList.length) return null;

      const avgMinutes = Math.round(
        minutesList.reduce((sum, value) => sum + value, 0) / minutesList.length
      );
      const hrs = Math.floor(avgMinutes / 60);
      const mins = avgMinutes % 60;

      return (hrs > 0 ? `${hrs}h ` : '') + `${mins}m`;
    },

    statusBreakdownChart() {
      const list = this.listVisitors || [];
      if (!list.length) return null;

      let completed = 0;
      let checkedIn = 0;
      let awaiting = 0;

      list.forEach((item) => {
        if (item.checkin_time && item.checkout_time) {
          completed++;
        } else if (item.checkin_time && !item.checkout_time) {
          checkedIn++;
        } else {
          awaiting++;
        }
      });

      return [
        ["Status", "Visits"],
        ["Checked Out", completed],
        ["Checked In", checkedIn],
        ["Awaiting", awaiting],
      ];
    },

    facilityBreakdownChart() {
      const list = this.listVisitors || [];
      if (!list.length) return null;

      const counts = {};
      list.forEach((item) => {
        const key = item.host_facility || "Unspecified";
        counts[key] = (counts[key] || 0) + 1;
      });

      const rows = Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8);

      if (!rows.length) return null;

      return [["Facility", "Visits"], ...rows];
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    startDateWatcher() {
      this.editedItem.start_date = this.formatDate(this.startDateWatcher);
    },
    clarificationDateWatcher() {
      this.editedItem.clarification_end_date = this.formatDate(
        this.clarificationDateWatcher
      );
    },
    evaluationDateWatcher() {
      this.editedItem.evaluation_end_date = this.formatDate(
        this.evaluationDateWatcher
      );
    },
    approvalDateWatcher() {
      this.editedItem.tender_approval_date = this.formatDate(
        this.approvalDateWatcher
      );
    },
    options: {
      handler () {
        this.getAllReports();
      },
      deep: true,
    },
    search (val) {
      if (!val || val.length <= 3) return;

      if (this.isLoading) return;
      this.isLoading = true;
      this.getGuests(val);
    },
    filterReportBySearch(val) {
      clearTimeout(this.searchDebounce);

      this.searchDebounce = setTimeout(() => {
        this.search_term = (val || '').trim();

        // reset to first page when searching
        this.options = {
          ...this.options,
          page: 1,
        };

        this.getAllReports();
      }, 300);
    },
  },

  created() {
    this.$nextTick(() => {
      this.getAllReports();
      this.filterTableVariables();
      this.getWatchmen();
      this.getOrganizations();
      this.getFacilities();
    });
  },

  methods: {
    getGuests(val) {
      UserService.searchUser(val).then(
        (response) => {
          if (response.status == 200) {
            this.guests = response.data?.data;
            this.isLoading = false;
            this.loading = false;
          }
        },
        (error) => {
          this.guests = [];

          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    getOrganizations() {
      let user = JSON.parse(window.localStorage.getItem('user'));

      OrganizationService.personOrganizations(user.id).then(
        (response) => {
          if (response.status == 200) {
            this.organizations = response.data.data;
            this.loading = false;
          }
        },
        (error) => {
          this.organizations = [];
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    getFacilities() {
      let orgId = this.org?.id;
      FacilitiesService.index(orgId).then(
        (response) => {
          if (response.status == 200) {
            this.facilities = response.data.data;
            this.loading = false;
          }
        },
        (error) => {
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    getWatchmen() {
      this.loading = true;
      WatchmenService.index().then(
        (response) => {
          if (response.status === 200) {
            this.watchmenList = response.data.data;
            this.loading = false;
          } else {
            this.watchmenList = [];
          }
        },
        (error) => {
          this.watchmenList = [];
          if (error.response.status === 401) {
            this.handleLogout();
          }
        }
      );
    },

    onTimePickerSelect() {
      this.$refs.dialog.save(this.filters.filter_time);
      if (this.filters.filter_time && this.filters.filter_time != 'null') {
        this.onFilterSelect(this.filterTime);
      }
    },

    onDatePickerSelect() {
      this.filters.filter_date = [...this.filters.filter_date].sort();
      this.activeDatePreset = null;
      this.$refs.datePickerDialog.save(this.filters.filter_date);
      this.onFilterSelect();
    },

    /**
     * Set filters.filter_date to an ISO [start, end] range matching one of the
     * quick presets, using the same array format the date-range picker uses.
     */
    applyDatePreset(preset) {
      const today = moment();
      let from;
      let to;

      if (preset === 'today') {
        from = today.clone().format('YYYY-MM-DD');
        to = from;
      } else if (preset === 'week') {
        from = today.clone().startOf('week').format('YYYY-MM-DD');
        to = today.clone().endOf('week').format('YYYY-MM-DD');
      } else if (preset === 'month') {
        from = today.clone().startOf('month').format('YYYY-MM-DD');
        to = today.clone().endOf('month').format('YYYY-MM-DD');
      } else {
        return;
      }

      this.activeDatePreset = preset;
      this.filters.filter_date = [from, to];
      this.onFilterSelect();
    },

    clearDatePreset() {
      this.activeDatePreset = null;
      this.filters.filter_date = [];
      this.onFilterSelect();
    },

    getAllReports() {
      return this.$store.dispatch('reports/list', { 
        options: this.options,
        filters: this.filters,
        search_term: this.search_term,
      });
    },

    /**
     * Parse durations formatted like "2 Hrs 30 Mins" / "45 Mins" into minutes.
     */
    parseDurationMinutes(duration) {
      if (!duration || typeof duration !== 'string') return null;

      const hoursMatch = duration.match(/(\d+)\s*Hrs?/i);
      const minsMatch = duration.match(/(\d+)\s*Mins?/i);

      if (!hoursMatch && !minsMatch) return null;

      const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
      const mins = minsMatch ? parseInt(minsMatch[1], 10) : 0;

      return hours * 60 + mins;
    },

    buildCsv(rows) {
      var visitorsReport = (rows || []).map(function (item) {
        return {
          visitor_name: item.visitor?.name,
          host_name: item.host?.name,
          checkin_time: item.checkin_time?.replace(/,/g, ""),
          checkout_time: item.checkout_time?.replace(/,/g, ""),
          duration: item.duration,
          host_facility: item.host_facility,
          reason: item.reason, 
          status: item.visitor?.status?.toUpperCase(),
          view: '#' + item.id,
        }
      });

      let csv = this.headersMap.map((header) => header.text)?.toString().toUpperCase() + '\n';
      visitorsReport?.map(Object.values).forEach((row) => {
        csv += row.join(',');
        csv += "\n";
      });

      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = new Date().getTime() + '.csv';
      anchor.click();
    },

    /**
     * Export the full filtered result set (not just the current page) by
     * requesting a large per_page from the API; falls back to the rows
     * already loaded on the page if that request fails.
     */
    downloadCSV() {
      this.downloading = true;

      const payload = {
        options: { itemsPerPage: 1000, page: 1 },
        filters: this.filters,
        search_term: this.search_term,
      };

      ReportsService.listAll(payload, this.filters?.watchman_id).then(
        (response) => {
          const rows = response?.data?.data?.data;
          this.buildCsv(Array.isArray(rows) && rows.length ? rows : this.listVisitors);
          this.downloading = false;
        },
        (error) => {
          console.log(error);
          this.buildCsv(this.listVisitors);
          this.downloading = false;
        }
      );
    },

    selectedColumn(column) {
      return this.selectedHeaders.includes(column)
    },

    viewVisit(item) {
      this.$router.push({
        name: `ViewReport`,
        params: { visitor: item }
      });
    },

    /**
     * Assign report filter with specific resources used in filtering
     */
    filterTableVariables() {
      if (this.listVisitors) {
        const dateSet = new Set();
        const hostOrganizationSet = new Set();
        const timeSet = new Set();
        const facilitySet = new Set();
        const guestsSet = new Set();

        this.hosts = [];

        this.listVisitors.forEach(item => {
          this.hosts.push(item.host);
          dateSet.add(item.event_date);
          hostOrganizationSet.add(item.visitor.organization);
          timeSet.add(item.start_at);
          facilitySet.add(item.host_facility);
          guestsSet.add(item.visitor.name);
        });

        // convert set to array
        this.date = Array.from(dateSet);
        this.time = Array.from(timeSet);
      }
    },

    onFilterSelect() {
      this.$store.dispatch('reports/filter', {
        options: this.options,
        filters: this.filters,
        search_term: this.search_term,
      });
    },

    getNestedValue(obj, path) {
      return path
        .split(".")
        .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
    },

    tableSearchFilter(value, search, item) {
      if (!search) return true;
      const term = String(search).toLowerCase().trim();

      return this.headersMap.some((header) => {
        const v = this.getNestedValue(item, header.value);
        return v !== null && v !== undefined && String(v).toLowerCase().includes(term);
      });
    },
  },

};
</script>
